import { useState, useEffect, useCallback } from "react";
import type { User } from "firebase/auth";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { getFirebaseAuth, getFirebaseFirestore } from "@/config/firebase";
import { setErrorTrackingUser, trackError } from "@/utils/error-tracking";
import { setAnalyticsUser, clearAnalyticsUser } from "@/utils/analytics-tracking";

/** 데모 계정 이메일 패턴 (demo-*@vlab-pilates.test) */
const DEMO_EMAIL_PATTERN = /^demo-.*@vlab-pilates\.test$/;

export interface AdminUser {
  uid: string;
  email: string;
  name: string;
  /** admin-web 접근 가능한 role. owner(원장)와 admin(직원)을 구분해 보존한다. */
  role: "admin" | "owner";
  /** 데모 계정 여부 */
  isDemo: boolean;
}

/** 원장 전환 대기 상태의 강사 정보 (needs-upgrade) */
export interface PendingUpgradeUser {
  uid: string;
  email: string;
  name: string;
}

export type AuthState =
  | { status: "loading" }
  | { status: "authenticated"; user: AdminUser }
  /** role=instructor — 강제 로그아웃 대신 원장 전환 화면으로 보낸다 */
  | { status: "needs-upgrade"; pending: PendingUpgradeUser }
  | { status: "unauthenticated" };

/** 프로필 role에 따른 처리 분기 */
type ProfileResolution =
  | { kind: "admin"; user: AdminUser }
  | { kind: "needs-upgrade"; pending: PendingUpgradeUser }
  /** 프로필 없음 / 처리 불가 role → 로그아웃 */
  | { kind: "reject" };

async function loadUserProfile(uid: string, email: string): Promise<ProfileResolution> {
  const snap = await getDoc(doc(getFirebaseFirestore(), "users", uid));
  if (!snap.exists()) return { kind: "reject" };

  const profile = snap.data();

  if (profile.role === "admin" || profile.role === "owner") {
    return {
      kind: "admin",
      user: {
        uid,
        email,
        name: profile.name,
        role: profile.role === "owner" ? "owner" : "admin",
        isDemo: DEMO_EMAIL_PATTERN.test(email),
      },
    };
  }

  // 강사는 본인 계정을 owner로 전환할 수 있다(upgradeToOwner). 로그아웃하지 않는다.
  if (profile.role === "instructor") {
    return { kind: "needs-upgrade", pending: { uid, email, name: profile.name } };
  }

  return { kind: "reject" };
}

/**
 * onAuthStateChanged 콜백의 핵심 로직.
 * 테스트 가능성을 위해 hook에서 분리했다.
 */
export async function handleAuthUser(
  firebaseUser: { uid: string; email: string | null },
  setState: (state: AuthState) => void,
): Promise<void> {
  try {
    const result = await loadUserProfile(firebaseUser.uid, firebaseUser.email || "");
    if (result.kind === "admin") {
      setErrorTrackingUser(result.user.uid, result.user.role);
      setAnalyticsUser(result.user.uid, result.user.role);
      setState({ status: "authenticated", user: result.user });
    } else if (result.kind === "needs-upgrade") {
      // 강사 계정 — 로그아웃하지 않고 원장 전환 화면으로 보낸다.
      setState({ status: "needs-upgrade", pending: result.pending });
    } else {
      // 프로필 없음 / 처리 불가 role → 로그아웃
      await firebaseSignOut(getFirebaseAuth());
      setState({ status: "unauthenticated" });
    }
  } catch (error: unknown) {
    console.error("[Auth] 프로필 로드 실패:", error);
    trackError(error, { source: "auth", action: "loadUserProfile" });
    setState({ status: "unauthenticated" });
  }
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getFirebaseAuth(), async (firebaseUser: User | null) => {
      if (!firebaseUser) {
        setState({ status: "unauthenticated" });
        return;
      }

      await handleAuthUser(firebaseUser, setState);
    });

    return () => unsubscribe();
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setState({ status: "loading" });
    await signInWithEmailAndPassword(getFirebaseAuth(), email, password);
  }, []);

  const signOut = useCallback(async () => {
    clearAnalyticsUser();
    await firebaseSignOut(getFirebaseAuth());
    setState({ status: "unauthenticated" });
  }, []);

  /**
   * 현재 로그인 사용자의 프로필을 재평가한다.
   *
   * upgradeToOwner 성공 직후 호출하면 Firestore의 갱신된 role(=owner)을 다시 읽어
   * needs-upgrade → authenticated 로 전환한다. role은 custom claim이 아니라 Firestore
   * `users/{uid}`에 있으므로 토큰 갱신은 불필요하다.
   */
  const refresh = useCallback(async () => {
    const current = getFirebaseAuth().currentUser;
    if (!current) {
      setState({ status: "unauthenticated" });
      return;
    }
    setState({ status: "loading" });
    await handleAuthUser({ uid: current.uid, email: current.email }, setState);
  }, []);

  return { ...state, signIn, signOut, refresh };
}
